import { MAX_PLAYERS } from "../src/protocol";
import type { Lang } from "../src/languages";
import { LANG } from "./i18n";

// Every visible text lives here, once per language. The `Strings` type is derived from the
// English table, so a key missing in Spanish is a compile error.
//
// Wording: WorkBrawl avoids "game"/"play" vocabulary (some workplace filters block it).
// Say round, room, start, people, title.

const en = {
  appName: "SnakeBrawl",
  pageTitle: "SnakeBrawl — Real-time snake showdown for teams",
  tagline: "The real-time snake showdown",
  homeIntro: "Open a room, share the link and outlast your coworkers before the clock runs out.",
  uiLanguageLabel: "Language",
  backToHub: "Back to WorkBrawl",
  privacy: "Privacy",
  terms: "Terms",
  consentText: "We use analytics cookies to learn how WorkBrawl is used. They\'re off unless you accept.",
  consentAccept: "Accept",
  consentReject: "Reject",
  consentNotice: "We use Google Analytics cookies to understand how WorkBrawl is used and improve it.",
  consentGotIt: "Got it",

  newRoomTitle: "New room",
  nameLabel: "Your name",
  namePlaceholder: "e.g. Alex",
  nameInvalid: "Enter a name of 1 to 20 characters.",
  durationLabel: "Round length",
  minutes: (n: number) => `${n} minutes`,
  createRoom: "Create room",
  creatingRoom: "Creating…",
  verifying: "Checking…",
  or: "or",
  codeLabel: "Room code",
  codePlaceholder: "ABCDE",
  codeInvalid: "Room codes have 5 letters or numbers.",
  join: "Join",
  createFailed: "Could not create the room. Try again.",
  joinFailed: "Could not check the room. Try again.",
  turnstileFailed: "The security check failed. Try again.",
  turnstileUnavailable: "The security check is unavailable. Reload the page and try again.",
  turnstileInteract: "Tick the box below to confirm you're a person.",
  errorRoomNotFound: "This room does not exist or has expired.",
  errorRoomFull: `This room is full (${MAX_PLAYERS} people).`,
  invalidRoomLink: "That room link isn't valid.",

  howTitle: "How it works",
  howSteps: [
    { title: "Open a room.", text: "Pick how long the round lasts, then share the link or the QR code." },
    { title: "Steer your snake.", text: "Use the arrow keys or WASD, or swipe on a phone. The edges wrap around." },
    { title: "Score before time is up.", text: "Apples and bananas are 1 point and cherries are 3. You also get 3 when someone crashes into you." },
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Is SnakeBrawl free?", a: "Yes. It's free, needs no account or download and runs in any modern browser." },
    { q: "How many people fit in a room?", a: `Up to ${MAX_PLAYERS} people per room, each on their own device.` },
    { q: "What happens when I crash?", a: "You keep your points and come back a couple of seconds later with a short snake." },
    { q: "Does it work on a phone?", a: "Yes. Swipe anywhere on the arena to turn." },
  ],

  namePromptTitle: "What's your name?",
  namePromptBody: "This is how the others will see you on the scoreboard.",
  enterRoom: "Enter room",

  room: "Room",
  exit: "Leave",
  cancel: "Cancel",
  exitTitle: "Leave the room?",
  exitBody: "You'll go back to the start screen. Come back with the room link to rejoin.",
  scoreboard: "Scoreboard",
  timeLeft: "Time left",
  you: "you",
  offline: "away",
  points: (n: number) => (n === 1 ? "1 point" : `${n} points`),

  inviteTitle: "Invite your team",
  copyLink: "Copy link",
  linkCopied: "Copied!",
  linkCopiedToast: "Link copied",
  copyFailed: "Copy this link:",
  copyFailedShort: "Couldn't copy",
  share: "Share",
  shareText: "Join me on SnakeBrawl, the real-time snake showdown!",
  close: "Close",
  qrLabel: (code: string) => `QR code to join room ${code}`,

  lobbyTitle: "Ready when you are",
  lobbyBody: (n: number) => (n === 1 ? "You're the only one here. Share the link, or start on your own." : `${n} people in the room.`),
  startRound: "Start round",
  controlsHint: "Arrow keys or WASD · swipe on a phone",
  fruitPoints: (n: number) => (n === 1 ? "1 point" : `${n} points`),
  getReady: "Get ready",
  respawning: "Back in a moment…",
  resultsTitle: "Time's up!",
  winner: (name: string) => `${name} wins`,
  youWin: "You win!",
  tie: "It's a tie",
  newRound: "New round",

  connecting: "Connecting…",
  reconnecting: "Connection lost. Reconnecting…",
  backHome: "Back to start",
};

export type Strings = typeof en;

const es: Strings = {
  appName: "SnakeBrawl",
  pageTitle: "SnakeBrawl — Duelo de serpientes en tiempo real para equipos",
  tagline: "El duelo de serpientes en tiempo real",
  homeIntro: "Abre una sala, comparte el enlace y resiste más que tu equipo antes de que se acabe el tiempo.",
  uiLanguageLabel: "Idioma",
  backToHub: "Volver a WorkBrawl",
  privacy: "Privacidad",
  terms: "Términos",
  consentText: "Usamos cookies de analítica para saber cómo se usa WorkBrawl. Están desactivadas salvo que las aceptes.",
  consentAccept: "Aceptar",
  consentReject: "Rechazar",
  consentNotice: "Usamos cookies de Google Analytics para entender cómo se usa WorkBrawl y mejorarlo.",
  consentGotIt: "Entendido",

  newRoomTitle: "Nueva sala",
  nameLabel: "Tu nombre",
  namePlaceholder: "p. ej. Álex",
  nameInvalid: "Escribe un nombre de 1 a 20 caracteres.",
  durationLabel: "Duración de la ronda",
  minutes: (n: number) => `${n} minutos`,
  createRoom: "Crear sala",
  creatingRoom: "Creando…",
  verifying: "Verificando…",
  or: "o",
  codeLabel: "Código de sala",
  codePlaceholder: "ABCDE",
  codeInvalid: "Los códigos de sala tienen 5 letras o números.",
  join: "Entrar",
  createFailed: "No se pudo crear la sala. Inténtalo de nuevo.",
  joinFailed: "No se pudo comprobar la sala. Inténtalo de nuevo.",
  turnstileFailed: "La verificación de seguridad falló. Inténtalo de nuevo.",
  turnstileUnavailable: "La verificación de seguridad no está disponible. Recarga la página e inténtalo de nuevo.",
  turnstileInteract: "Marca la casilla de abajo para confirmar que eres una persona.",
  errorRoomNotFound: "Esta sala no existe o ya expiró.",
  errorRoomFull: `Esta sala está llena (${MAX_PLAYERS} personas).`,
  invalidRoomLink: "El enlace de la sala no es válido.",

  howTitle: "Cómo funciona",
  howSteps: [
    { title: "Abre una sala.", text: "Elige cuánto dura la ronda y comparte el enlace o el código QR." },
    { title: "Dirige tu serpiente.", text: "Usa las flechas o WASD, o desliza el dedo en el teléfono. Los bordes se conectan." },
    { title: "Suma antes de que acabe el tiempo.", text: "Las manzanas y los plátanos valen 1 punto y las cerezas 3. También ganas 3 cuando alguien choca contra ti." },
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿SnakeBrawl es gratis?", a: "Sí. Es gratis, no necesita cuenta ni descarga y funciona en cualquier navegador moderno." },
    { q: "¿Cuántas personas caben en una sala?", a: `Hasta ${MAX_PLAYERS} personas por sala, cada una en su propio dispositivo.` },
    { q: "¿Qué pasa si choco?", a: "Conservas tus puntos y vuelves un par de segundos después con una serpiente corta." },
    { q: "¿Funciona en el teléfono?", a: "Sí. Desliza el dedo en cualquier parte de la arena para girar." },
  ],

  namePromptTitle: "¿Cómo te llamas?",
  namePromptBody: "Así te verán los demás en el marcador.",
  enterRoom: "Entrar a la sala",

  room: "Sala",
  exit: "Salir",
  cancel: "Cancelar",
  exitTitle: "¿Salir de la sala?",
  exitBody: "Volverás al inicio. Puedes regresar con el enlace de la sala.",
  scoreboard: "Marcador",
  timeLeft: "Tiempo restante",
  you: "tú",
  offline: "ausente",
  points: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),

  inviteTitle: "Invita a tu equipo",
  copyLink: "Copiar enlace",
  linkCopied: "¡Copiado!",
  linkCopiedToast: "Enlace copiado",
  copyFailed: "Copia este enlace:",
  copyFailedShort: "No se pudo copiar",
  share: "Compartir",
  shareText: "¡Únete a SnakeBrawl, el duelo de serpientes en tiempo real!",
  close: "Cerrar",
  qrLabel: (code: string) => `Código QR para entrar a la sala ${code}`,

  lobbyTitle: "Cuando quieran",
  lobbyBody: (n: number) => (n === 1 ? "Por ahora solo estás tú. Comparte el enlace, o empieza por tu cuenta." : `${n} personas en la sala.`),
  startRound: "Empezar ronda",
  controlsHint: "Flechas o WASD · desliza el dedo en el teléfono",
  fruitPoints: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),
  getReady: "Prepárate",
  respawning: "Vuelves en un momento…",
  resultsTitle: "¡Se acabó el tiempo!",
  winner: (name: string) => `Gana ${name}`,
  youWin: "¡Ganaste!",
  tie: "Empate",
  newRound: "Nueva ronda",

  connecting: "Conectando…",
  reconnecting: "Se perdió la conexión. Reconectando…",
  backHome: "Volver al inicio",
};

const TABLES: Record<Lang, Strings> = { en, es };

export const STRINGS: Strings = TABLES[LANG];

/** Each language written in itself, for the language switcher. */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" };
